/**
 * HTML paste sanitizer for TipTap.
 *
 * Removes the noise that Word / Google Docs / LibreOffice / OpenOffice ship
 * with their pasted HTML so the editor gets clean, semantic content.
 *
 * Strategies applied in order:
 *  1. Strip <!--StartFragment/EndFragment--> and MSO conditional comments.
 *  2. Delete <style>, <script>, <meta>, <link>, <o:p>, <w:*>, <xml> tags.
 *  3. Unwrap <font>, <span> (whose only value was a Word style), <b>/<i>
 *     without content.
 *  4. Convert Word's fake bullets (paragraphs with mso-list styles and a
 *     bullet character) into a real <ul><li>.
 *  5. Strip all `style`, `class`, `id`, `lang`, `align`, `dir`, and Office-
 *     specific attributes (mso-*, xmlns:*, w:*, v:*).
 *  6. Preserve semantic tags: h1-h6, p, ul/ol/li, table/tr/td/th, blockquote,
 *     img (src+alt), a (href), b/strong/em/i/u/s/code/pre.
 *  7. Sanitise the final result with DOMPurify to remove anything unsafe.
 */

import DOMPurify from 'dompurify';
import { marked } from 'marked';

/* ---------- backward-compat: markdown -> HTML for legacy content ---------- */

export function isProbablyMarkdown(text = '') {
  if (!text) return false;
  const s = String(text).trim();
  if (!s) return false;
  // Contains block HTML tags? Not markdown.
  if (/<(p|div|h[1-6]|ul|ol|blockquote|table|section|article|figure)\b/i.test(s)) return false;
  // Markdown fingerprints
  if (/^#{1,6}\s/m.test(s)) return true;
  if (/^\s*[-*+]\s+/m.test(s)) return true;
  if (/\*\*[^*]+\*\*/.test(s)) return true;
  if (/```/.test(s)) return true;
  if (/^\s*\d+\.\s+/m.test(s)) return true;
  return false;
}

export function markdownToHtml(md = '') {
  try {
    marked.setOptions({ gfm: true, breaks: true });
    return DOMPurify.sanitize(marked.parse(md), { USE_PROFILES: { html: true } });
  } catch (_e) {
    return '';
  }
}

/* ------------------------- paste sanitizer -------------------------------- */

const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'mark', 'small',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'span', 'div',  // kept so purify has something to unwrap
];
const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'colspan', 'rowspan'];

function stripOfficeNoise(html) {
  return html
    // MSO conditional / structure comments
    .replace(/<!--\[if[\s\S]*?\[endif\]-->/g, '')
    .replace(/<!--StartFragment-->/g, '')
    .replace(/<!--EndFragment-->/g, '')
    // Word / OpenOffice specific containers
    .replace(/<o:p[\s\S]*?<\/o:p>/gi, '')
    .replace(/<o:p[^>]*>/gi, '')
    .replace(/<\/?(w|o|v|m|x):[^>]*>/gi, '')
    .replace(/<xml[\s\S]*?<\/xml>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    // Google Docs wraps EVERYTHING in <b id="docs-internal-guid-...">.
    // The HTML parser closes the <b> early when it hits a block-level child,
    // orphaning the content. Strip the wrapper tag as raw text so the block
    // children end up at the top level.
    .replace(/<b [^>]*id=["']docs-internal-guid-[^"']*["'][^>]*>/gi, '');
}

/**
 * Convert Word's fake bullets (each list item is a <p class="MsoListParagraph">
 * beginning with a bullet character) into a proper <ul><li> structure.
 */
function stripLeadingMarker(el) {
  // Walk from the start and remove text/elements that make up the bullet marker
  // "·  ", "1) ", "• ", "1. ", etc. Handles Word's spans-around-a-bullet.
  const isMarkerText = (t) => /^[\s\u00a0]*([•·▪◦■●*\-]|\d+[.)])[\s\u00a0]*$/.test(t);
  const markerRegex = /^[\s\u00a0]*([•·▪◦■●*\-]|\d+[.)])[\s\u00a0]+/;
  let safety = 12;
  while (safety-- > 0 && el.firstChild) {
    const n = el.firstChild;
    if (n.nodeType === 3 /* TEXT */) {
      const t = n.textContent || '';
      if (!t.trim()) { el.removeChild(n); continue; }
      if (markerRegex.test(t)) {
        n.textContent = t.replace(markerRegex, '');
        if (!n.textContent) el.removeChild(n);
        return;
      }
      return; // reached real text
    }
    if (n.nodeType === 1 /* ELEMENT */) {
      const t = (n.textContent || '');
      if (!t.trim()) { el.removeChild(n); continue; }
      if (isMarkerText(t)) { el.removeChild(n); continue; }
      // Element contains the marker followed by real text: recurse into it.
      const clone = n.cloneNode(true);
      stripLeadingMarker(clone);
      n.replaceWith(clone);
      return;
    }
    el.removeChild(n);
  }
}

function fixWordBullets(root) {
  const doc = root.ownerDocument;
  const paras = Array.from(root.querySelectorAll('p'));
  let i = 0;
  while (i < paras.length) {
    const p = paras[i];
    const looksList =
      (p.className && /Mso(ListParagraph|List)/i.test(p.className)) ||
      (p.getAttribute('style') || '').match(/mso-list/i);
    if (!looksList) { i++; continue; }

    // Collect the run of consecutive list-paragraphs.
    const runOl = /^[\s\u00a0]*\d+[.)]/.test(p.textContent);
    const run = [];
    let j = i;
    while (j < paras.length) {
      const q = paras[j];
      const isList =
        (q.className && /Mso(ListParagraph|List)/i.test(q.className)) ||
        (q.getAttribute('style') || '').match(/mso-list/i);
      if (!isList) break;
      run.push(q);
      j++;
    }
    const list = doc.createElement(runOl ? 'ol' : 'ul');
    for (const q of run) {
      const li = doc.createElement('li');
      // Copy children then strip the leading bullet/number marker.
      while (q.firstChild) li.appendChild(q.firstChild);
      stripLeadingMarker(li);
      list.appendChild(li);
    }
    p.parentNode.insertBefore(list, p);
    run.forEach((q) => q.remove());
    i = j;
  }
}

/**
 * Unwrap spans / divs / fonts that had no semantic value in Word / Docs.
 */
function unwrapEmptyWrappers(root) {
  const wrappers = ['span', 'font', 'div'];
  wrappers.forEach((tag) => {
    Array.from(root.querySelectorAll(tag)).forEach((el) => {
      // Preserve if it has meaningful attributes remaining after stripping.
      const hasHref = el.getAttribute('href');
      const hasSrc = el.getAttribute('src');
      if (hasHref || hasSrc) return;
      // Unwrap
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });
  });
}

/**
 * Strip all attributes except a small allow-list. Runs after purify but is
 * a cheap extra guarantee against style="mso-..." leftovers.
 */
function stripJunkAttributes(root) {
  const walker = root.ownerDocument.createTreeWalker(root, 1);
  const keep = new Set(ALLOWED_ATTR);
  let node = walker.currentNode;
  while (node) {
    if (node.attributes && node.attributes.length) {
      for (const attr of Array.from(node.attributes)) {
        if (!keep.has(attr.name.toLowerCase())) node.removeAttribute(attr.name);
      }
    }
    node = walker.nextNode();
  }
}

/**
 * Google Docs wraps everything in <b id="docs-internal-guid-...">.
 * That fake bold is a container, not styling — unwrap it.
 */
function unwrapGoogleDocsBold(root) {
  Array.from(root.querySelectorAll('b[id^="docs-internal-guid-"]')).forEach((b) => {
    const parent = b.parentNode;
    while (b.firstChild) parent.insertBefore(b.firstChild, b);
    parent.removeChild(b);
  });
}

/**
 * Drop empty paragraphs and paragraphs that contain only &nbsp; / whitespace.
 */
function dropEmptyBlocks(root) {
  Array.from(root.querySelectorAll('p,div')).forEach((el) => {
    const txt = (el.textContent || '').replace(/\u00a0/g, '').trim();
    if (!txt && !el.querySelector('img,br,hr,table')) el.remove();
  });
}

export function sanitizePastedHtml(rawHtml) {
  if (!rawHtml) return '';

  // Fast paths for tiny / non-HTML pastes.
  if (!/<[a-z][\s\S]*>/i.test(rawHtml)) return rawHtml;

  const cleaned = stripOfficeNoise(rawHtml);

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__paste_root">${cleaned}</div>`, 'text/html');
  const root = doc.getElementById('__paste_root');
  if (!root) return cleaned;

  unwrapGoogleDocsBold(root);
  fixWordBullets(root);
  unwrapEmptyWrappers(root);
  stripJunkAttributes(root);
  dropEmptyBlocks(root);

  // Final safety pass with DOMPurify.
  return DOMPurify.sanitize(root.innerHTML, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ['style', 'class', 'id', 'lang', 'dir', 'align', 'width', 'height', 'border', 'cellpadding', 'cellspacing'],
    KEEP_CONTENT: true,
  });
}

/* ------------- Article renderer helper (used on public pages) ------------- */

export function articleToSafeHtml(content) {
  if (!content) return '';
  if (isProbablyMarkdown(content)) return markdownToHtml(content);
  return DOMPurify.sanitize(content, {
    ADD_ATTR: ['target', 'rel'],
  });
}
